/**
 * Typed wrappers for the TONADO jetton master and wallet. Mirrors
 * `pool.ts` style. The on-chain contracts live in
 * `apps/contracts/contracts/jetton/{master,wallet}.tolk`; this file is
 * the off-chain counterpart used by deploy scripts, tests, and the SDK.
 *
 * Storage shape MUST match the Tolk contracts byte-for-byte. If you
 * change one side, change the other; see master.tolk :: JettonMasterStorage
 * and wallet.tolk :: JettonWalletStorage.
 */
import { Address, beginCell, SendMode, toNano, } from "@ton/core";
// ============================================================================
// Op codes (jetton master + wallet + admin)
// ============================================================================
export const JETTON_MASTER_OPS = {
    // TEP-74 / TEP-89 inbound
    BURN_NOTIFICATION: 0x7bdd97de,
    PROVIDE_WALLET_ADDRESS: 0x2c76b973,
    TAKE_WALLET_ADDRESS: 0xd1735400,
    // Admin
    ADMIN_MINT: 0xded05810,
    ADMIN_CHANGE_ADMIN: 0xded05811,
    ADMIN_CLAIM_ADMIN: 0xded05812,
    ADMIN_CHANGE_CONTENT: 0xded05813,
    ADMIN_DISABLE_MINT: 0xded05814,
};
export const JETTON_WALLET_OPS = {
    TRANSFER: 0x0f8a7ea5,
    INTERNAL_TRANSFER: 0x178d4519,
    BURN: 0x595f07bc,
    EXCESSES: 0xd53276db,
    TRANSFER_NOTIFICATION: 0x7362d09c,
};
// ============================================================================
// Common constants
// ============================================================================
/** Canonical "burned" address. Same constant the pool uses for unbound
 *  jetton-wallet sentinel and credibly-neutral owner. */
export const JETTON_BURN_ADDRESS = new Address(0, Buffer.alloc(32, 0));
// ============================================================================
// Jetton master
// ============================================================================
export class TonadoJettonMaster {
    address;
    init;
    constructor(address, init) {
        this.address = address;
        this.init = init;
    }
    static at(address) {
        return new TonadoJettonMaster(address);
    }
    static BURN_ADDRESS = JETTON_BURN_ADDRESS;
    /**
     * Build the master's initial data cell. MUST match
     * `master.tolk :: JettonMasterStorage` byte-for-byte.
     *
     * Storage layout (inline):
     *   coins   totalSupply
     *   bool    mintable
     *   address admin
     *   address pendingAdmin       (BURN_ADDRESS sentinel by default)
     *   cell    jettonContent      (TEP-64 metadata)
     *   cell    jettonWalletCode   (wallet code, used to derive wallet addrs)
     */
    static buildInitialData(opts) {
        return beginCell()
            .storeCoins(opts.totalSupply ?? 0n)
            .storeUint(opts.mintable === false ? 0 : 1, 1)
            .storeAddress(opts.admin)
            .storeAddress(opts.pendingAdmin ?? JETTON_BURN_ADDRESS)
            .storeRef(opts.jettonContent)
            .storeRef(opts.jettonWalletCode)
            .endCell();
    }
    static fromInit(opts) {
        const data = TonadoJettonMaster.buildInitialData({
            totalSupply: opts.totalSupply,
            mintable: opts.mintable,
            admin: opts.admin,
            pendingAdmin: opts.pendingAdmin,
            jettonContent: opts.jettonContent,
            jettonWalletCode: opts.jettonWalletCode,
        });
        const stateInitCell = beginCell()
            .storeUint(0, 2)
            .storeUint(3, 2)
            .storeRef(opts.code)
            .storeRef(data)
            .storeUint(0, 1)
            .endCell();
        const wc = opts.workchain ?? 0;
        return new TonadoJettonMaster(new Address(wc, stateInitCell.hash()), {
            code: opts.code,
            data,
        });
    }
    /**
     * Compute the deterministic jetton wallet address for `owner` under
     * this master. Off-chain mirror of `master.tolk :: walletDeploy()` —
     * must produce the byte-identical state-init the master would.
     *
     * Wallet initial data (fresh, balance=0):
     *   coins   balance = 0
     *   address owner
     *   address jettonMasterAddress
     */
    static computeWalletAddress(opts) {
        const initialData = beginCell()
            .storeCoins(0)
            .storeAddress(opts.owner)
            .storeAddress(opts.master)
            .endCell();
        const stateInitCell = beginCell()
            .storeUint(0, 2)
            .storeUint(3, 2)
            .storeRef(opts.walletCode)
            .storeRef(initialData)
            .storeUint(0, 1)
            .endCell();
        return new Address(opts.workchain ?? 0, stateInitCell.hash());
    }
    // ---- Send: admin mint ----
    async sendAdminMint(provider, via, opts) {
        const walletMessageValue = opts.walletMessageValue ?? toNano("0.1");
        const forwardPayload = opts.forwardPayload ?? beginCell().endCell();
        const body = beginCell()
            .storeUint(JETTON_MASTER_OPS.ADMIN_MINT, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeAddress(opts.recipient)
            .storeCoins(opts.jettonAmount)
            .storeCoins(walletMessageValue)
            .storeCoins(opts.forwardTon ?? 0n)
            .storeRef(forwardPayload)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? walletMessageValue + toNano("0.1"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    // ---- Send: admin change/claim/content/disable-mint ----
    async sendAdminChangeAdmin(provider, via, opts) {
        const body = beginCell()
            .storeUint(JETTON_MASTER_OPS.ADMIN_CHANGE_ADMIN, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeAddress(opts.newAdmin)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminClaimAdmin(provider, via, opts = {}) {
        const body = beginCell()
            .storeUint(JETTON_MASTER_OPS.ADMIN_CLAIM_ADMIN, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminChangeContent(provider, via, opts) {
        const body = beginCell()
            .storeUint(JETTON_MASTER_OPS.ADMIN_CHANGE_CONTENT, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeRef(opts.newContent)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminDisableMint(provider, via, opts = {}) {
        const body = beginCell()
            .storeUint(JETTON_MASTER_OPS.ADMIN_DISABLE_MINT, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    // ---- Get methods ----
    async getJettonData(provider) {
        const res = await provider.get("get_jetton_data", []);
        return {
            totalSupply: res.stack.readBigNumber(),
            mintable: res.stack.readNumber() !== 0,
            admin: res.stack.readAddress(),
            content: res.stack.readCell(),
            walletCode: res.stack.readCell(),
        };
    }
    async getWalletAddress(provider, owner) {
        const ownerCell = beginCell().storeAddress(owner).endCell();
        const res = await provider.get("get_wallet_address", [
            { type: "slice", cell: ownerCell },
        ]);
        return res.stack.readAddress();
    }
    async getAdmin(provider) {
        const res = await provider.get("getAdmin", []);
        return res.stack.readAddress();
    }
    async getPendingAdmin(provider) {
        const res = await provider.get("getPendingAdmin", []);
        return res.stack.readAddress();
    }
    async isMintable(provider) {
        const res = await provider.get("isMintable", []);
        return res.stack.readNumber() !== 0;
    }
}
// ============================================================================
// Jetton wallet
// ============================================================================
export class TonadoJettonWallet {
    address;
    init;
    constructor(address, init) {
        this.address = address;
        this.init = init;
    }
    static at(address) {
        return new TonadoJettonWallet(address);
    }
    /**
     * Build a wallet's initial data cell for owner `owner` under master
     * `master`. MUST match `wallet.tolk :: JettonWalletStorage` for the
     * balance=0 case.
     */
    static buildInitialData(opts) {
        return beginCell()
            .storeCoins(opts.balance ?? 0n)
            .storeAddress(opts.owner)
            .storeAddress(opts.master)
            .endCell();
    }
    static fromInit(opts) {
        const data = TonadoJettonWallet.buildInitialData({
            balance: opts.balance,
            owner: opts.owner,
            master: opts.master,
        });
        const stateInitCell = beginCell()
            .storeUint(0, 2)
            .storeUint(3, 2)
            .storeRef(opts.code)
            .storeRef(data)
            .storeUint(0, 1)
            .endCell();
        return new TonadoJettonWallet(new Address(opts.workchain ?? 0, stateInitCell.hash()), { code: opts.code, data });
    }
    // ---- Send: transfer (owner-initiated) ----
    async sendTransfer(provider, via, opts) {
        const customPayload = opts.customPayload ?? null;
        const forwardPayload = opts.forwardPayload ?? beginCell().endCell();
        const b = beginCell()
            .storeUint(JETTON_WALLET_OPS.TRANSFER, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeCoins(opts.jettonAmount)
            .storeAddress(opts.destination)
            .storeAddress(opts.responseDestination);
        if (customPayload === null) {
            b.storeBit(false);
        }
        else {
            b.storeBit(true).storeRef(customPayload);
        }
        b.storeCoins(opts.forwardTon ?? 0n).storeBit(true).storeRef(forwardPayload);
        await provider.internal(via, {
            value: opts.value ?? toNano("0.1"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: b.endCell(),
        });
    }
    // ---- Send: burn (owner-initiated) ----
    async sendBurn(provider, via, opts) {
        const customPayload = opts.customPayload ?? null;
        const b = beginCell()
            .storeUint(JETTON_WALLET_OPS.BURN, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeCoins(opts.jettonAmount)
            .storeAddress(opts.responseDestination);
        if (customPayload === null) {
            b.storeBit(false);
        }
        else {
            b.storeBit(true).storeRef(customPayload);
        }
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: b.endCell(),
        });
    }
    // ---- Get methods ----
    async getWalletData(provider) {
        const res = await provider.get("get_wallet_data", []);
        return {
            balance: res.stack.readBigNumber(),
            owner: res.stack.readAddress(),
            master: res.stack.readAddress(),
            walletCode: res.stack.readCell(),
        };
    }
    async getBalance(provider) {
        const res = await provider.get("getBalance", []);
        return res.stack.readBigNumber();
    }
    async getOwner(provider) {
        const res = await provider.get("getOwner", []);
        return res.stack.readAddress();
    }
    async getMaster(provider) {
        const res = await provider.get("getMaster", []);
        return res.stack.readAddress();
    }
}
//# sourceMappingURL=tonadoJetton.js.map